import { useCreateTransactionReason, useRefreshTransactionReasons, useUpdateTransactionReason } from '@app/sdk';
import { FormContainer, Input, InputProps, Select } from '@app/shared';
import { ComponentErrorBoundary } from '@app/utilities';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

type TransactionReasonFormModel = {
  id: string;
  reason: string;
  transactionType: string;
  transactionDirections: string;
  description: string;
  transactionSource?: string;
};

export function TransactionReasonForm({ transactionReason }: { transactionReason: TransactionReasonFormModel }) {
  const { mutateAsync: create, isPending: isCreating } = useCreateTransactionReason();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateTransactionReason();
  const refreshTransactionReasons = useRefreshTransactionReasons();

  async function onSaveReason(data: TransactionReasonFormModel) {
    if (data.transactionSource === '') {
      data.transactionSource = null;
    }

    if (transactionReason?.id) {
      const request = {
        ...transactionReason,
        ...data,
        transactionDirections: data.transactionDirections
          ?.split(',')
          .map((x) => x.trim())
          .filter((x) => x),
      };
      await update({ id: transactionReason.id, request });
    } else {
      const request = {
        ...data,
        transactionDirections: data.transactionDirections
          ?.split(',')
          .map((x) => x.trim())
          .filter((x) => x),
      };
      await create(request);
    }
    refreshTransactionReasons();
  }

  const formContext = useForm<TransactionReasonFormModel>();

  const reason: InputProps<TransactionReasonFormModel> = {
    name: 'reason',
    label: 'Reason',
    options: { required: 'Reason is required' },
    defaultValue: transactionReason?.reason,
  };

  const description: InputProps<TransactionReasonFormModel> = {
    name: 'description',
    label: 'Description',
    options: { required: 'Description is required' },
    defaultValue: transactionReason?.description,
  };

  const transactionType: InputProps<TransactionReasonFormModel> = {
    name: 'transactionType',
    label: 'Type',
    options: { required: 'Type is required' },
    defaultValue: transactionReason?.transactionType,
  };

  const transactionDirections: InputProps<TransactionReasonFormModel> = {
    name: 'transactionDirections',
    label: 'Directions(Comma Separated)',
    options: { required: 'Directions is required' },
    defaultValue: transactionReason?.transactionDirections,
  };

  const [selectedTransactionSource, setSelectedTransactionSource] = useState<string | null>(null);
  const transactionSourceOptions = [
    { value: 'RegularPayment', label: 'RegularPayment' },
    { value: 'PayOnline', label: 'No PayOnline' },
    { value: 'OpenBanking', label: 'OpenBanking' },
    { value: 'CardPayment', label: 'CardPayment' },
    { value: 'CheckOut', label: 'CheckOut' },
    { value: 'BankTransfer', label: 'BankTransfer' },
    { value: 'FasterPayment', label: 'FasterPayment' },
  ];

  function setTransactionSource(transactionSource: string) {
    setSelectedTransactionSource(transactionSource);
  }

  return (
    <ComponentErrorBoundary>
      <FormProvider {...formContext}>
        <FormContainer isSubmitting={isCreating || isUpdating} onSubmit={onSaveReason} disableButtons>
          <Input {...reason} disabled />
          <Input {...description} disabled />
          <Input {...transactionType} disabled />
          <Input {...transactionDirections} disabled />
          <Select
            disabled
            name="transactionSource"
            selectOptions={transactionSourceOptions}
            label="Transaction Source"
            value={selectedTransactionSource}
            onChange={(v) => setTransactionSource(v.target.value)}
          />
        </FormContainer>
      </FormProvider>
    </ComponentErrorBoundary>
  );
}
